import { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { API } from "@/lib/api";

const SID = Math.random().toString(36).slice(2);
const HLS_URL = `${API}/hls/live.m3u8?sid=${SID}`;

export const EQ_BANDS = [[60, "lowshelf", "60"], [230, "peaking", "230"], [910, "peaking", "910"], [3600, "peaking", "3.6K"], [14000, "highshelf", "14K"]];
export const EQ_PRESETS = {
  FLAT: [0, 0, 0, 0, 0], "SUPER BASS": [9, 5, -1, 0, 2], LOWRIDER: [7, 3, -1, 1, 3], VOCAL: [-2, -1, 3, 4, 1],
  CLUB: [5, 2, 0, 2, 4], SMOOTH: [3, 1, 0, -1, -2], BRIGHT: [-1, 0, 1, 4, 6],
};
const loadEq = () => { try { const v = JSON.parse(localStorage.getItem("eq-gains")); return Array.isArray(v) && v.length === 5 ? v : EQ_PRESETS.FLAT; } catch { return EQ_PRESETS.FLAT; } };
const isNative = (a) => !!a?.canPlayType("application/vnd.apple.mpegurl") && (navigator.vendor || "").includes("Apple");
const preamp = (gains) => Math.pow(10, (-Math.max(0, ...gains) * 0.3) / 20); // a little headroom so boosts don't clip

// Plays the station over HLS (short segment requests survive proxy connection limits).
export function useLiveAudio(audioRef) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eq, setEq] = useState(loadEq);
  const hls = useRef(null);
  const wanted = useRef(false);
  const graph = useRef(null);
  const eqRef = useRef(eq);
  const eqSupported = Hls.isSupported() && !isNative(document.createElement("audio"));

  useEffect(() => {
    eqRef.current = eq;
    localStorage.setItem("eq-gains", JSON.stringify(eq));
    const g = graph.current;
    if (!g) return;
    g.filters.forEach((f, i) => f.gain.setTargetAtTime(eq[i], g.ctx.currentTime, 0.05));
    g.pre.gain.setTargetAtTime(preamp(eq), g.ctx.currentTime, 0.05);
  }, [eq]);

  const teardown = useCallback(() => {
    hls.current?.destroy();
    hls.current = null;
    const a = audioRef.current;
    if (a) { a.pause(); a.removeAttribute("src"); a.load(); }
  }, [audioRef]);

  // Web Audio graph (EQ + analyser) only on the hls.js path (native iOS HLS can't be routed and must keep background playback).
  const attachGraph = useCallback(() => {
    if (graph.current) { graph.current.ctx.resume(); return; }
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaElementSource(audioRef.current);
      const filters = EQ_BANDS.map(([freq, type], i) => {
        const f = ctx.createBiquadFilter();
        f.type = type; f.frequency.value = freq; f.Q.value = 1.1; f.gain.value = eqRef.current[i];
        return f;
      });
      const pre = ctx.createGain();
      pre.gain.value = preamp(eqRef.current);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      [src, pre, ...filters, analyser, ctx.destination].reduce((a, b) => { a.connect(b); return b; });
      graph.current = { ctx, analyser, filters, pre };
    } catch {}
  }, [audioRef]);

  const connect = useCallback(() => {
    const a = audioRef.current;
    teardown();
    if (isNative(a) || !Hls.isSupported()) {
      a.src = HLS_URL;
    } else {
      attachGraph();
      const h = new Hls({
        liveSyncDurationCount: 3, liveMaxLatencyDurationCount: 7, maxLiveSyncPlaybackRate: 1, backBufferLength: 20,
        manifestLoadingMaxRetry: 20, levelLoadingMaxRetry: 20, fragLoadingMaxRetry: 20, manifestLoadingRetryDelay: 1000,
      });
      h.on(Hls.Events.ERROR, (_, d) => {
        if (!d.fatal) return;
        if (d.type === Hls.ErrorTypes.MEDIA_ERROR) h.recoverMediaError();
        else setTimeout(() => wanted.current && connect(), 2000);
      });
      h.loadSource(HLS_URL);
      h.attachMedia(a);
      hls.current = h;
    }
    return a.play();
  }, [audioRef, teardown, attachGraph]);

  useEffect(() => {
    const a = audioRef.current;
    const retry = () => { if (wanted.current) setTimeout(() => wanted.current && connect().catch(() => {}), 1500); };
    a.addEventListener("ended", retry);
    a.addEventListener("error", retry);
    return () => { a.removeEventListener("ended", retry); a.removeEventListener("error", retry); teardown(); };
  }, [audioRef, connect, teardown]);

  const toggle = useCallback(async () => {
    if (wanted.current) {
      wanted.current = false;
      teardown();
      setPlaying(false);
      return;
    }
    wanted.current = true;
    setLoading(true);
    try { await connect(); setPlaying(true); } catch { wanted.current = false; setPlaying(false); }
    setLoading(false);
  }, [connect, teardown]);

  // Stall watchdog: if audio stops advancing while we want playback, reconnect to the live edge.
  useEffect(() => {
    let last = -1, stuck = 0;
    const t = setInterval(() => {
      const a = audioRef.current;
      if (!wanted.current || !a) { stuck = 0; return; }
      if (a.currentTime === last) stuck += 4; else stuck = 0;
      last = a.currentTime;
      if (stuck >= 12) { stuck = 0; connect().catch(() => {}); }
    }, 4000);
    return () => clearInterval(t);
  }, [audioRef, connect]);

  const analyser = useCallback(() => (hls.current && graph.current ? graph.current.analyser : null), []);
  const latency = useCallback(() => {
    const a = audioRef.current;
    if (hls.current && Number.isFinite(hls.current.latency)) return hls.current.latency + 0.5;
    if (a?.seekable?.length) return a.seekable.end(a.seekable.length - 1) - a.currentTime + 1;
    return 0;
  }, [audioRef]);

  return { playing, loading, toggle, analyser, latency, eq, setEq, eqSupported };
}
